/**
 * API-key hooks back the admin and caller-owned surfaces. Create responses are
 * the only API responses that carry the secret token; list responses contain
 * prefixes only.
 */

import { type ApiKeysListData, hubQueryKeys } from "@lifter/hub-ui/browser-shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type AgentQueryOptions, useHubApi } from "./context";

function activeKeys(data: ApiKeysListData): ApiKeysListData {
  return { ...data, keys: data.keys.filter((key) => key.revokedAt === null) };
}

export function useApiKeysQuery(options: AgentQueryOptions = {}) {
  const api = useHubApi();
  return useQuery({
    queryKey: hubQueryKeys.apiKeys(),
    queryFn: () => api.apiKeys.list(),
    select: activeKeys,
    enabled: options.enabled ?? true,
  });
}

export function useCreateApiKeyMutation() {
  const api = useHubApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: { name: string; userId: string }) => api.apiKeys.create(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: hubQueryKeys.apiKeys() });
    },
  });
}

export function useRevokeApiKeyMutation() {
  const api = useHubApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (keyId: string) => api.apiKeys.revoke(keyId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: hubQueryKeys.apiKeys() });
    },
  });
}

export function useMyApiKeysQuery(options: AgentQueryOptions = {}) {
  const api = useHubApi();
  return useQuery({
    queryKey: hubQueryKeys.myApiKeys(),
    queryFn: () => api.myApiKeys.list(),
    select: activeKeys,
    enabled: options.enabled ?? true,
  });
}

export function useCreateMyApiKeyMutation() {
  const api = useHubApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: { name: string }) => api.myApiKeys.create(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: hubQueryKeys.myApiKeys() });
    },
  });
}

export function useRevokeMyApiKeyMutation() {
  const api = useHubApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (keyId: string) => api.myApiKeys.revoke(keyId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: hubQueryKeys.myApiKeys() });
    },
  });
}
